import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const BasicInfoForm = ({ formData, onUpdate, onNext }) => {
  const [errors, setErrors] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  // Organized vendor categories with subcategories like browse-categories page
  const vendorCategories = [
    {
      id: 'entertainment',
      title: 'Entertainment',
      icon: 'Music',
      categories: [
        { id: 'dj-services', label: 'DJ Services', icon: 'Music', description: 'South Asian music experts: Bollywood, Bhangra, Urdu, Tamil, Fusion & more' },
        { id: 'dhol-bhangra', label: 'Dhol Players & Bhangra Teams', icon: 'Drum', description: 'Traditional dhol players and energetic performers' },
        { id: 'mc-hosts', label: 'MCs & Hosts', icon: 'Mic', description: 'Bilingual wedding hosts and ceremony guides' },
        { id: 'live-bands', label: 'Live Bands / Singers', icon: 'Users', description: 'Live musical performances and traditional singers' },
        { id: 'folk-dancers', label: 'Folk Dancers / Cultural Performers', icon: 'Activity', description: 'Traditional dance and cultural entertainment' }
      ]
    },
    {
      id: 'photography',
      title: 'Photography & Videography', 
      icon: 'Camera',
      categories: [
        { id: 'wedding-photographers', label: 'Wedding Photographers', icon: 'Camera', description: 'Professional wedding photography specialists' },
        { id: 'videographers', label: 'Cinematic Videographers', icon: 'Video', description: 'Cinematic wedding videography and storytelling' },
        { id: 'photo-booth', label: 'Photo Booth Providers', icon: 'Image', description: 'Interactive photo booth experiences' },
        { id: 'drone-coverage', label: 'Drone Coverage', icon: 'Plane', description: 'Aerial photography and videography services' }
      ]
    },
    {
      id: 'beauty',
      title: 'Beauty & Grooming',
      icon: 'Palette', 
      categories: [
        { id: 'bridal-makeup', label: 'Bridal Makeup Artists', icon: 'Palette', description: 'Professional bridal makeup for South Asian weddings' },
        { id: 'hair-stylists', label: 'Bridal Hair Stylists', icon: 'Scissors', description: 'Traditional and modern bridal hair styling' },
        { id: 'mehndi-artists', label: 'Mehndi / Henna Artists', icon: 'Brush', description: 'Beautiful henna designs for brides and guests' },
        { id: 'groom-services', label: 'Groom Services', icon: 'User', description: 'Grooming and styling services for grooms' }
      ]
    },
    {
      id: 'venues-catering',
      title: 'Venues & Catering',
      icon: 'Building',
      categories: [
        { id: 'banquet-halls', label: 'Banquet Halls', icon: 'Building', description: 'Traditional and modern wedding venue spaces' },
        { id: 'destination-venues', label: 'Destination / Outdoor Venues', icon: 'MapPin', description: 'Unique outdoor and destination wedding locations' },
        { id: 'caterers', label: 'Catering Services', icon: 'ChefHat', description: 'Authentic South Asian cuisine with dietary accommodations' },
        { id: 'sweet-shops', label: 'Sweet Shops / Dessert Vendors', icon: 'Cake', description: 'Traditional sweets and dessert specialists' }
      ]
    },
    {
      id: 'decor',
      title: 'Decor & Production',
      icon: 'Flower',
      categories: [
        { id: 'event-decor', label: 'Event Decor / Floral Designers', icon: 'Flower', description: 'Beautiful floral arrangements and event decoration' },
        { id: 'mandap-design', label: 'Mandap / Stage Design', icon: 'Home', description: 'Traditional mandap and stage design services' },
        { id: 'led-screens', label: 'LED Screens / Visual FX', icon: 'Monitor', description: 'Modern audio-visual and lighting solutions' },
        { id: 'furniture-rentals', label: 'Lounge Furniture / Rentals', icon: 'Armchair', description: 'Elegant furniture and rental services' }
      ]
    },
    {
      id: 'planning',
      title: 'Planning & Logistics',
      icon: 'Calendar',
      categories: [
        { id: 'wedding-planners', label: 'Full-Service Wedding Planners', icon: 'Calendar', description: 'Complete wedding planning and coordination' },
        { id: 'day-coordinators', label: 'Day-of Coordinators', icon: 'Clock', description: 'Professional wedding day coordination services' },
        { id: 'budget-planning', label: 'Budget Planning Experts', icon: 'DollarSign', description: 'Wedding budget planning and financial guidance' },
        { id: 'baraat-rentals', label: 'Luxury Car / Horse Baraat Rentals', icon: 'Car', description: 'Traditional and luxury transportation for groom' }
      ]
    }
  ];

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleVendorTypeToggle = (typeId) => {
    const currentTypes = formData.vendorTypes || [];
    const newTypes = currentTypes.includes(typeId)
      ? currentTypes.filter(type => type !== typeId)
      : [...currentTypes, typeId];
    
    onUpdate({ vendorTypes: newTypes });
    if (errors.vendorTypes) {
      setErrors(prev => ({ ...prev, vendorTypes: null }));
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vendorTypes?.length) newErrors.vendorTypes = 'Please select at least one vendor type';
    if (!formData.businessName?.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.contactName?.trim()) newErrors.contactName = 'Contact name is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state?.trim()) newErrors.state = 'State is required';

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter phone in format: (123) 456-7890';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange('phone', formatted);
  };

  return (
    <div className="space-y-8">
      {/* Vendor Type Selection - Using browse-categories layout */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          What type of vendor are you?
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Select all categories that describe your wedding services. You can choose multiple specializations.
        </p>
        
        <div className="space-y-6">
          {vendorCategories.map((section) => (
            <div key={section.id} className="space-y-4">
              {/* Section Header */}
              <button
                onClick={() => toggleCategory(section.id)}
                className="flex items-center justify-between w-full p-4 bg-surface-50 rounded-xl hover:bg-surface-100 transition-smooth"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                    <Icon name={section.icon} size={20} color="#FFFFFF" />
                  </div>
                  <h4 className="font-heading font-semibold text-lg text-text-primary">
                    {section.title}
                  </h4>
                </div>
                <Icon 
                  name={expandedCategories[section.id] ? "ChevronUp" : "ChevronDown"} 
                  size={20} 
                  color="#6B5B4F" 
                />
              </button>

              {/* Subcategories Grid */}
              {(expandedCategories[section.id] !== false) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pl-4">
                  {section.categories.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleVendorTypeToggle(type.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-smooth hover:shadow-soft ${
                        formData.vendorTypes?.includes(type.id)
                          ? 'border-primary bg-primary-50 shadow-soft'
                          : 'border-border hover:border-primary-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                          formData.vendorTypes?.includes(type.id)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-surface-100 text-text-secondary'
                        }`}>
                          <Icon name={type.icon} size={16} color="currentColor" />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-medium text-sm ${
                            formData.vendorTypes?.includes(type.id) ? 'text-primary' : 'text-text-primary'
                          }`}>
                            {type.label}
                          </h4>
                          <p className="text-xs text-text-muted font-caption mt-1">
                            {type.description}
                          </p>
                        </div>
                        {formData.vendorTypes?.includes(type.id) && (
                          <Icon name="Check" size={16} color="#8B4513" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Selected count */}
        {formData.vendorTypes?.length > 0 && (
          <div className="mt-4 p-3 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary font-medium">
              Selected: {formData.vendorTypes.length} service{formData.vendorTypes.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
        
        {errors.vendorTypes && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.vendorTypes}</span>
          </p>
        )}
      </div>

      {/* Business Information */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Business Information
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Tell us about your business and how couples can reach you
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Business Name *
            </label>
            <Input
              type="text"
              placeholder="Enter your business name"
              value={formData.businessName || ''}
              onChange={(e) => handleInputChange('businessName', e.target.value)}
              className={errors.businessName ? 'border-error' : ''}
            />
            {errors.businessName && (
              <p className="text-error text-sm font-caption mt-1">{errors.businessName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Contact Person Name *
            </label>
            <Input
              type="text"
              placeholder="Your full name"
              value={formData.contactName || ''}
              onChange={(e) => handleInputChange('contactName', e.target.value)}
              className={errors.contactName ? 'border-error' : ''}
            />
            {errors.contactName && (
              <p className="text-error text-sm font-caption mt-1">{errors.contactName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Email Address *
            </label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-error' : ''}
            />
            {errors.email && (
              <p className="text-error text-sm font-caption mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Phone Number *
            </label>
            <Input
              type="tel"
              placeholder="(123) 456-7890"
              value={formData.phone || ''}
              onChange={handlePhoneChange}
              className={errors.phone ? 'border-error' : ''}
            />
            {errors.phone && (
              <p className="text-error text-sm font-caption mt-1">{errors.phone}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Website (Optional)
            </label>
            <Input
              type="url"
              placeholder="https://yourwebsite.com"
              value={formData.website || ''}
              onChange={(e) => handleInputChange('website', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Instagram Handle (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-text-muted">@</span>
              </div>
              <Input
                type="text"
                placeholder="yourbusiness"
                value={formData.instagram || ''}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Location Information
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Where is your business located? This helps couples find you
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              City *
            </label>
            <Input
              type="text"
              placeholder="Enter your city"
              value={formData.city || ''}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={errors.city ? 'border-error' : ''}
            />
            {errors.city && (
              <p className="text-error text-sm font-caption mt-1">{errors.city}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              State *
            </label>
            <select
              value={formData.state || ''}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                errors.state ? 'border-error' : 'border-border'
              }`}
            >
              <option value="">Select State</option>
              <option value="CA">California</option>
              <option value="NY">New York</option>
              <option value="TX">Texas</option>
              <option value="FL">Florida</option>
              <option value="IL">Illinois</option>
              <option value="NJ">New Jersey</option>
              <option value="PA">Pennsylvania</option>
              <option value="GA">Georgia</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="MD">Maryland</option>
              <option value="NC">North Carolina</option>
              <option value="MI">Michigan</option>
              <option value="OH">Ohio</option>
              <option value="AZ">Arizona</option>
            </select>
            {errors.state && (
              <p className="text-error text-sm font-caption mt-1">{errors.state}</p>
            )}
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Business Address (Optional)
            </label>
            <Input
              type="text"
              placeholder="Street address (will not be publicly displayed)"
              value={formData.address || ''}
              onChange={(e) => handleInputChange('address', e.target.value)}
            />
            <p className="text-xs text-text-muted font-caption mt-1">
              Your full address is kept private and only used for verification purposes
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end pt-6 border-t border-border">
        <Button
          variant="primary"
          size="lg"
          onClick={handleNext}
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={18}
        >
          Continue to Services
        </Button>
      </div>
    </div>
  );
};

export default BasicInfoForm;